import React, { useState, useEffect, useCallback, useRef } from 'react';
import RecruiterHeader from './RecruiterHeader';
import { talentPoolAPI, jobsAPI } from '../../utils/api';
import './TalentPoolView.css';

const SOURCE_OPTIONS = ['upload', 'linkedin', 'naukri', 'indeed', 'referral', 'campus', 'other'];
const STATUS_OPTIONS = ['available', 'in_process', 'hired', 'not_interested'];
const STATUS_LABELS = { available: 'Available', in_process: 'In Process', hired: 'Hired', not_interested: 'Not Interested' };
const STATUS_COLORS = { available: '#10b981', in_process: '#f59e0b', hired: '#3b82f6', not_interested: '#ef4444' };

// ── Upload Wizard ─────────────────────────────────────────────────────────────
function UploadWizard({ onDone, onCancel }) {
  const [step, setStep] = useState('drop'); // drop | parsing | review | saving | done
  const [files, setFiles] = useState([]);
  const [parsed, setParsed] = useState([]);
  const [source, setSource] = useState('upload');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const dropRef = useRef();

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.type === 'application/pdf' ||
      f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      f.type === 'application/msword'
    );
    setFiles(prev => [...prev, ...dropped].slice(0, 50));
  }, []);

  const handleFileInput = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selected].slice(0, 50));
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const parseFiles = async () => {
    if (files.length === 0) return;
    setStep('parsing');
    setError(null);
    try {
      const form = new FormData();
      files.forEach(f => form.append('resumes', f));
      const res = await talentPoolAPI.parse(form);
      setParsed(res.data.parsed.map(p => ({ ...p, selected: !p.parseError && !p.duplicate })));
      setStep('review');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStep('drop');
    }
  };

  const updateParsed = (idx, field, value) => {
    setParsed(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const confirmSave = async () => {
    const toSave = parsed.filter(p => p.selected && p.email && p.name);
    if (toSave.length === 0) return;
    setStep('saving');
    try {
      const res = await talentPoolAPI.confirm(toSave, source);
      setResult(res.data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStep('review');
    }
  };

  if (step === 'drop') return (
    <div className="wizard-overlay">
      <div className="wizard-box">
        <h2>Upload Resumes</h2>
        <div
          ref={dropRef}
          className="drop-zone"
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => document.getElementById('resume-file-input').click()}
        >
          <div className="drop-icon">&#8679;</div>
          <p>Drag & drop PDF or DOCX files here</p>
          <p className="drop-sub">Up to 50 files at once, 10 MB per file</p>
          <input id="resume-file-input" type="file" multiple accept=".pdf,.doc,.docx" onChange={handleFileInput} style={{ display: 'none' }} />
        </div>
        {files.length > 0 && (
          <div className="file-list">
            {files.map((f, i) => (
              <div key={i} className="file-item">
                <span className="file-name">{f.name}</span>
                <span className="file-size">{(f.size / 1024).toFixed(0)} KB</span>
                <button className="file-remove" onClick={() => removeFile(i)}>x</button>
              </div>
            ))}
          </div>
        )}
        {error && <p className="wizard-error">{error}</p>}
        <div className="wizard-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={files.length === 0} onClick={parseFiles}>
            Parse {files.length} File{files.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );

  if (step === 'parsing') return (
    <div className="wizard-overlay">
      <div className="wizard-box wizard-center">
        <div className="spinner-large" />
        <p>Parsing resumes...</p>
        <p className="drop-sub">This may take a moment for large batches</p>
      </div>
    </div>
  );

  if (step === 'review') return (
    <div className="wizard-overlay">
      <div className="wizard-box wizard-wide">
        <h2>Review Parsed Candidates</h2>
        <p className="drop-sub">
          {parsed.filter(p => p.selected).length} of {parsed.length} selected &mdash; edit any field before saving
        </p>
        <div className="source-row">
          <label>Source:</label>
          <select value={source} onChange={e => setSource(e.target.value)}>
            {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div className="review-table-wrap">
          <table className="review-table">
            <thead>
              <tr>
                <th></th>
                <th>File</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Skills</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map((p, i) => (
                <tr key={i} className={p.parseError ? 'row-error' : p.duplicate ? 'row-duplicate' : ''}>
                  <td>
                    {p.parseError
                      ? <span className="badge-error" title={p.parseError}>!</span>
                      : <input type="checkbox" checked={!!p.selected} onChange={e => updateParsed(i, 'selected', e.target.checked)} />
                    }
                  </td>
                  <td className="td-filename">{p.resumeFileName}</td>
                  <td><input value={p.name || ''} onChange={e => updateParsed(i, 'name', e.target.value)} className="cell-input" /></td>
                  <td><input value={p.email || ''} onChange={e => updateParsed(i, 'email', e.target.value)} className="cell-input" /></td>
                  <td><input value={p.phone || ''} onChange={e => updateParsed(i, 'phone', e.target.value)} className="cell-input cell-sm" /></td>
                  <td><input value={p.currentRole || ''} onChange={e => updateParsed(i, 'currentRole', e.target.value)} className="cell-input" /></td>
                  <td>
                    <input
                      value={(p.skills || []).join(', ')}
                      onChange={e => updateParsed(i, 'skills', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                      className="cell-input"
                      placeholder="comma-separated"
                    />
                  </td>
                  <td>
                    {p.parseError
                      ? <span className="badge-error">Parse failed</span>
                      : p.duplicate
                        ? <span className="badge-warn">Duplicate</span>
                        : <span className="badge-ok">New</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {error && <p className="wizard-error">{error}</p>}
        <div className="wizard-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={parsed.filter(p => p.selected).length === 0} onClick={confirmSave}>
            Save {parsed.filter(p => p.selected).length} Candidates
          </button>
        </div>
      </div>
    </div>
  );

  if (step === 'saving') return (
    <div className="wizard-overlay">
      <div className="wizard-box wizard-center">
        <div className="spinner-large" />
        <p>Saving candidates...</p>
      </div>
    </div>
  );

  if (step === 'done') return (
    <div className="wizard-overlay">
      <div className="wizard-box wizard-center">
        <div className="done-icon">&#10003;</div>
        <h2>Upload Complete</h2>
        <div className="result-stats">
          <div className="stat"><span className="stat-num">{result.saved}</span><span className="stat-label">Saved</span></div>
          <div className="stat"><span className="stat-num">{result.skipped}</span><span className="stat-label">Duplicates skipped</span></div>
          {result.errors?.length > 0 && <div className="stat"><span className="stat-num">{result.errors.length}</span><span className="stat-label">Errors</span></div>}
        </div>
        <button className="btn-primary" onClick={() => onDone()}>Done</button>
      </div>
    </div>
  );

  return null;
}

// ── Email Composer ────────────────────────────────────────────────────────────
function EmailComposer({ selectedIds, onClose, onSent }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [jobPostingId, setJobPostingId] = useState('');
  const [jobs, setJobs] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    jobsAPI.getJobs({ status: 'active', limit: 50 })
      .then(res => setJobs(res.data.jobs || []))
      .catch(() => {});
  }, []);

  const handleJobSelect = (e) => {
    const jobId = e.target.value;
    setJobPostingId(jobId);
    if (jobId) {
      const job = jobs.find(j => j._id === jobId);
      if (job) {
        setSubject(`Exciting opportunity: ${job.title} at SecureLayer7`);
        setBody(`Hi {{name}},\n\nWe have an exciting opening for the role of ${job.title} at SecureLayer7 that we think would be a great fit for you.\n\nWe'd love to reconnect and share more details.\n\nWould you be open to a quick call this week?\n\nBest regards,\nSecureLayer7 Recruitment Team`);
      }
    }
  };

  const send = async () => {
    if (!subject || !body) { setError('Subject and body required'); return; }
    setSending(true);
    setError(null);
    try {
      const res = await talentPoolAPI.sendEmail({ candidateIds: selectedIds, subject, body, jobPostingId: jobPostingId || undefined });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wizard-overlay">
      <div className="wizard-box">
        <h2>Send Email</h2>
        <p className="drop-sub">Sending to {selectedIds.length} candidate{selectedIds.length !== 1 ? 's' : ''}</p>

        {result ? (
          <div className="wizard-center">
            <div className="done-icon">&#10003;</div>
            <div className="result-stats">
              <div className="stat"><span className="stat-num">{result.sent}</span><span className="stat-label">Sent</span></div>
              {result.failed?.length > 0 && <div className="stat"><span className="stat-num">{result.failed.length}</span><span className="stat-label">Failed</span></div>}
            </div>
            <button className="btn-primary" onClick={onSent}>Done</button>
          </div>
        ) : (
          <>
            <div className="form-row">
              <label>Attach Job Posting (optional)</label>
              <select value={jobPostingId} onChange={handleJobSelect}>
                <option value="">-- Custom email --</option>
                {jobs.map(j => <option key={j._id} value={j._id}>{j.title}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject" />
            </div>
            <div className="form-row">
              <label>
                Body
                <span className="hint-text"> — use {'{{'+'name'+'}}'}, {'{{'+'job_title'+'}}'} as placeholders</span>
              </label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={8} placeholder="Email body..." />
            </div>
            {error && <p className="wizard-error">{error}</p>}
            <div className="wizard-actions">
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn-primary" disabled={sending} onClick={send}>
                {sending ? 'Sending...' : `Send to ${selectedIds.length}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────
const TalentPoolView = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await talentPoolAPI.list({
        search: search || undefined,
        status: filterStatus || undefined,
        source: filterSource || undefined,
        page,
        limit: LIMIT
      });
      setCandidates(res.data.candidates);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load talent pool:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterSource, page]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(candidates.map(c => c._id)));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await talentPoolAPI.update(id, { status });
      setCandidates(prev => prev.map(c => c._id === id ? { ...c, status } : c));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const deleteCandidate = async (id) => {
    if (!window.confirm('Remove this candidate from the talent pool?')) return;
    try {
      await talentPoolAPI.remove(id);
      setCandidates(prev => prev.filter(c => c._id !== id));
      setTotal(prev => prev - 1);
      setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="talent-pool-view">
      <RecruiterHeader />
      <div className="tp-content">
        <div className="tp-header">
          <div>
            <h1>Talent Pool</h1>
            <p className="tp-subtitle">{total} candidate{total !== 1 ? 's' : ''} in database</p>
          </div>
          <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload Resumes</button>
        </div>

        <div className="tp-filters">
          <input
            className="tp-search"
            placeholder="Search name, email, role, company..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
          <select value={filterSource} onChange={e => { setFilterSource(e.target.value); setPage(1); }}>
            <option value="">All Sources</option>
            {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>

        {selected.size > 0 && (
          <div className="bulk-bar">
            <span>{selected.size} selected</span>
            <button className="btn-secondary" onClick={() => setShowEmail(true)}>Send Email</button>
            <button className="btn-ghost" onClick={() => setSelected(new Set())}>Clear selection</button>
          </div>
        )}

        {loading ? (
          <div className="tp-loading"><div className="spinner-large" /></div>
        ) : candidates.length === 0 ? (
          <div className="tp-empty">
            <p>No candidates yet. Upload resumes to build your talent pool.</p>
            <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload Resumes</button>
          </div>
        ) : (
          <div className="tp-table-wrap">
            <table className="tp-table">
              <thead>
                <tr>
                  <th><input type="checkbox" checked={selected.size === candidates.length && candidates.length > 0} onChange={toggleAll} /></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Skills</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(c => (
                  <tr key={c._id} className={selected.has(c._id) ? 'row-selected' : ''}>
                    <td><input type="checkbox" checked={selected.has(c._id)} onChange={() => toggleSelect(c._id)} /></td>
                    <td>
                      <div className="candidate-name">{c.name}</div>
                      {c.currentCompany && <div className="candidate-company">{c.currentCompany}</div>}
                    </td>
                    <td className="td-email">{c.email}</td>
                    <td>{c.currentRole || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="skill-tags">
                        {(c.skills || []).slice(0, 3).map(s => <span key={s} className="skill-tag">{s}</span>)}
                        {(c.skills || []).length > 3 && <span className="skill-more">+{c.skills.length - 3}</span>}
                      </div>
                    </td>
                    <td><span className="source-badge">{c.source}</span></td>
                    <td>
                      <select
                        className="status-select"
                        value={c.status}
                        onChange={e => updateStatus(c._id, e.target.value)}
                        style={{ color: STATUS_COLORS[c.status] }}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </select>
                    </td>
                    <td>
                      <button className="btn-icon-danger" onClick={() => deleteCandidate(c._id)} title="Remove">&#10005;</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pages > 1 && (
          <div className="tp-pagination">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span>Page {page} of {pages}</span>
            <button disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {showUpload && (
        <UploadWizard
          onDone={() => { setShowUpload(false); load(); }}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {showEmail && (
        <EmailComposer
          selectedIds={[...selected]}
          onClose={() => setShowEmail(false)}
          onSent={() => { setShowEmail(false); setSelected(new Set()); load(); }}
        />
      )}
    </div>
  );
};

export default TalentPoolView;
